import type { Tower } from "../entities/Tower.ts";
import type { Projectile } from "../entities/Projectile.ts";
import type { Enemy } from "../entities/Enemy.ts";
import type { DraftSystem } from "./DraftSystem.ts";
import type { SynergySystem } from "./SynergySystem.ts";
import type { ObjectPool } from "../utils/ObjectPool.ts";
import type { EventBus } from "../core/EventBus.ts";
import type { Element } from "../entities/types.ts";
import type { HazardPoolZone } from "./HazardPool.ts";
import { findPierceTarget } from "./Targeting.ts";
import {
  applyInfectionZone,
  applyProjectileDamage,
} from "./DamageCalculation.ts";
import { makeHazardPool } from "./HazardPool.ts";
import { COMBAT } from "../core/config.ts";

const VIRUS_ARC_HEIGHT = 28;

export interface ProjectileCombatContext {
  projectilePool: ObjectPool<Projectile>;
  draftSystem: DraftSystem | null;
  synergySystem: SynergySystem | null;
  elementBonuses: Record<string, number>;
  towers: Tower[];
  enemies: Enemy[];
  bus: EventBus;
  bossDamageMult: number;
  globalDamageMult: number;
  piercingBonus: number;
  hazardPools: HazardPoolZone[];
  rng: () => number;
}

/** Same-type clustering penalty: 0.85^n for n same-element towers within 2 cells above 3. */
export function getClusterPenalty(tower: Tower, towers: Tower[]): number {
  const CLUSTER_RANGE_SQ = COMBAT.CLUSTER_RANGE * COMBAT.CLUSTER_RANGE;
  let sameCount = 0;
  for (const other of towers) {
    if (other === tower || other.element !== tower.element) continue;
    const dx = other.x - tower.x;
    const dy = other.y - tower.y;
    if (dx * dx + dy * dy <= CLUSTER_RANGE_SQ) sameCount++;
  }
  if (sameCount <= 2) return 1;
  return Math.pow(0.85, sameCount - 2);
}

/** Fire a projectile from a tower toward its target */
export function fireProjectile(
  tower: Tower,
  ctx: ProjectileCombatContext,
): void {
  const proj = ctx.projectilePool.acquire();
  const clusterPenalty = getClusterPenalty(tower, ctx.towers);
  // BL-175 / D-266 — fold signal's GOLD "Command Aura" buff into the muzzle damage
  // (the per-frame `commandAuraMult`, 1 with no ORACLE in range → byte-identical).
  proj.init(
    tower.x,
    tower.y,
    tower.target!,
    tower.projectileSpeed,
    Math.round(tower.damage * clusterPenalty * tower.commandAuraMult),
    tower.element as Element,
    tower.color,
    tower.special,
  );
  proj.pierceCount = ctx.piercingBonus;
  // BL-174 / D-263 — carry the firing tower's level onto the projectile so the
  // level-gated capabilities (fire Scorch splash, water Cryo-Chain slow) can
  // read it at impact, when the tower reference is long gone. `init()` does not
  // touch `level`, and `reset()` clears it to 1, so a sub-gate shot is byte-safe.
  proj.level = tower.level;
  ctx.bus.emit("tower.fired", { tower, projectile: proj });
}

/** Fire a ground-targeted lob projectile with no homing target. */
export function fireLobProjectile(
  tower: Tower,
  targetX: number,
  targetY: number,
  ctx: ProjectileCombatContext,
): void {
  const proj = ctx.projectilePool.acquire();
  proj.initLob(
    tower.x,
    tower.y,
    targetX,
    targetY,
    tower.projectileSpeed,
    tower.damage,
    tower.element as Element,
    tower.color,
    tower.special,
    VIRUS_ARC_HEIGHT,
    tower.level,
  );
  ctx.bus.emit("tower.fired", { tower, projectile: proj });
}

/** Advance all active projectiles by dt */
export function updateProjectiles(
  pool: ObjectPool<Projectile>,
  dt: number,
): void {
  for (const proj of pool.active) {
    proj.update(dt);
  }
}

/** Check projectile hits, apply damage, handle pierce */
export function checkProjectileHits(ctx: ProjectileCombatContext): void {
  const active = ctx.projectilePool.active;
  const virusMods = ctx.synergySystem?.getVirusSynergyMods() ?? null;
  for (let i = active.length - 1; i >= 0; i--) {
    const proj = active[i]!;
    if (!proj.active) {
      if (proj.lobbed) {
        const spawn = applyInfectionZone(
          proj.x,
          proj.y,
          proj.special,
          {
            enemies: ctx.enemies,
            bus: ctx.bus,
            virusMods,
          },
          proj.level,
        );
        if (spawn) {
          ctx.hazardPools.push(makeHazardPool(spawn.x, spawn.y, spawn.profile));
        }
        ctx.projectilePool.release(proj);
        continue;
      }

      applyProjectileDamage(proj, {
        draftSystem: ctx.draftSystem,
        synergySystem: ctx.synergySystem,
        elementBonuses: ctx.elementBonuses,
        towers: ctx.towers,
        enemies: ctx.enemies,
        bus: ctx.bus,
        bossDamageMult: ctx.bossDamageMult,
        globalDamageMult: ctx.globalDamageMult,
        rng: ctx.rng,
      });

      if (proj.pierceCount > 0) {
        if (proj.target) proj.hitEnemyVersions.add(proj.target.version);
        const nextTarget = findPierceTarget(proj, ctx.enemies);
        if (nextTarget) {
          proj.pierceCount--;
          proj.target = nextTarget;
          proj.targetVersion = nextTarget.version;
          proj.active = true;
          continue;
        }
      }

      ctx.projectilePool.release(proj);
    }
  }
}
